import React, { createContext, useState, useEffect, useContext } from 'react';
import { Roles, userDummyPrd } from '../utils/constants';
import { LoaderContext } from './LoaderContextProvider';
import { brandModel } from '../entities/brandModel';
import { userModel } from '../entities/userModel';
import { useAuth } from '../services/auth';
import { useNetwork } from 'services/network';

const AuthContext = createContext();

const AuthContextProvider = ({ children, onLoggedUser, onDetectedEmail }) => {
  const { getCurrentLoggedUserParams, getEmailFromUrl } = useAuth();
  const [user, setUser] = useState();
  const [processedEmail, setProcessedEmail] = useState();
  const [isEmailLoaded, setIsEmailLoaded] = useState(false);

  const { setLoaderActive } = useContext(LoaderContext);
  const network = useNetwork();
  //Loader Function
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);

  const getExternalIdFromParams = () => {
    const params = new URLSearchParams(location.search);
    const id = params.get('salesforce_id');
    return id;
  };

  const getCurrentLoggedUser = async () => {
    showLoader();
    let params;
    try {
      params = await getCurrentLoggedUserParams();
      params.role = params.is_brand
        ? Roles.BRAND
        : params.is_admin
        ? Roles.ADMIN
        : params.is_operator
        ? Roles.OPERATOR
        : Roles.USER;
    } catch (e) {
      // prettier-ignore
      //##token##
      console.error(e);
      onLoggedUser({});
      return setUser({});
    }
    const {
      email,
      role,
      field_brand_name: brand,
      field_nome: givenName,
      field_cognome: familyName,
      field_federationid_salesforce: salId,
      field_salesforceid_brand,
      available_services: services,
      roles,
      tk,
    } = params;

    //se il ruolo è brand, mi aspetto il param, a meno che non sia /exhibitors oppure analytics
    const isExhibitorsRoute =
      window.location?.pathname.includes('exhibitors') ||
      window.location?.pathname.includes('analytics');

    const isBrand = role === Roles.BRAND;

    const isGold = isBrand ? params?.roles?.includes('exhibitor_gold') : false;

    const isSilver = isBrand
      ? params?.roles?.includes('exhibitor_silver')
      : false;

    const externalId = isBrand
      ? isExhibitorsRoute
        ? salId
        : getExternalIdFromParams()
      : undefined;
    const federationId = isBrand
      ? params.field_federationid_salesforce
      : undefined;
    const lowerCaseRoles = roles?.length
      ? roles.map((r) => r.toLowerCase())
      : [];
    const isPrd =
      userDummyPrd === 'true' ||
      lowerCaseRoles.includes('produttore') ||
      lowerCaseRoles.includes('fornitore');

    const tempUser = {
      email,
      role,
      brand,
      givenName,
      familyName,
      externalId,
      federationId,
      salesforceIdBrand: field_salesforceid_brand || '',
      salId,
      isGold,
      isSilver,
      services,
      isPrd,
      tk,
    };
    //fetch the email from the url, it return the effective email or false
    const emailUrl = getEmailFromUrl();
    onDetectedEmail && onDetectedEmail(emailUrl);
    setProcessedEmail(emailUrl);
    setUser(tempUser);
    setIsEmailLoaded(true);
  };

  useEffect(() => {
    getCurrentLoggedUser();
  }, []);

  const fetchExhibitorData = async (salesforceId) => {
    try {
      const res = await network.get(
        `${network.getBaseUrl(false)}/api/brand/${salesforceId}/it`
      );
      if (res?.data?.[0]) return res.data[0]?.title;
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    isEmailLoaded && fetchUser();
  }, [isEmailLoaded]);

  const fetchUser = async () => {
    // eslint-disable-next-line
    let gql;
    let input;
    let customUser;
    let brandName;
    if (user.role !== Roles.ADMIN) {
      switch (user.role) {
        case Roles.BRAND:
          brandName =
            process?.env?.NODE_ENV !== 'development'
              ? await fetchExhibitorData(user.externalId)
              : null;
          input = {
            name: brandName || user.brand,
            email: user.email,
            externalId: user.externalId,
          };
          gql = brandModel.gql;
          customUser = await gql.get(user.externalId, true);
          break;
        default:
          input = {
            givenName: user.givenName,
            familyName: user.familyName,
            email: user.email,
          };
          gql = userModel.gql;
          customUser = await gql.get(user.email, null, false);
      }
      if (!customUser) {
        const justCreated = await gql.create(input);
        customUser = {
          ...input,
          ...justCreated,
        };
      } else {
        customUser = {
          ...customUser,
          ...input,
        };
        if (customUser?.salesTeam) delete customUser.salesTeam;
        await gql.update(customUser);
      }
    }
    hideLoader();
    const utn = {
      ...customUser,
      type: user.role,
      salId: user.salId,
      isGold: user.isGold || false,
      isSilver: user.isSilver || false,
      services: user.services || [],
      tk: user.tk,
      isPrd: user.isPrd || false,
      ...(user.role === Roles.BRAND && {
        federationId: user.federationId,
        salesforceIdBrand: user.salesforceIdBrand,
      }),
    };
    onLoggedUser && onLoggedUser(utn);
    setUser(utn);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        processedEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContextProvider, AuthContext };
